import fetch from "isomorphic-fetch";
import axios from "axios";
import { captureException } from "./sentry";

/*
 * Custom fetch function for fetching data on both client and server, with req.user available in both cases
 * */

const createFullURL = (url, ctx = {}) => {
  const { req } = ctx;
  const prefix = req
    ? `${req.protocol}://${req.get("host")}`
    : window.location.origin;
  return `${prefix}${url.charAt(0) === "/" ? "" : "/"}${url}`;
};

const awesomeFetch = async (url, ctx = {}) => {
  const { req } = ctx;
  const options = { credentials: "include" };
  req ? (options["headers"] = { cookie: req.headers["cookie"] }) : null;
  return fetch(createFullURL(url, ctx), options);
};

const createQueryFromJson = params =>
  Object.keys(params)
    .map(k => encodeURIComponent(k) + "=" + encodeURIComponent(params[k]))
    .join("&");

export default class {
  static createFullUrl(url, ctx) {
    return createFullURL(url, ctx);
  }

  static async getRecommendedProgramForForm(ctx = {}) {
    // isomorphic-fetch runs in browsers and server-side
    try {
      const res = await awesomeFetch(`api/v1.1/recommended/program`, ctx);
      const data = res.json();
      return data;
    } catch (e) {
      captureException(e, {});
      return null;
    }
  }

  // Added for the mission.js page
  static async getMission(slug, ctx = {}) {
    // isomorphic-fetch runs in browsers and server-side
    try {
      const res = await awesomeFetch(`api/v1.1/mission/${slug}`, ctx);
      const data = res.json();
      return data;
    } catch (e) {
      captureException(e, {});
      return null;
    }
  }

  //your cause api caller
  static async getYourCauseDataForSpecificSessionId(sessionId, ctx = {}) {
    try {
      const res = await awesomeFetch(`api/yc/fetchData/${sessionId}`, ctx);
      if (res.status !== 200) {
        return null;
      }
      const data = res.json();
      return data;
    } catch (e) {
      captureException(e, {});
      return null;
    }
  }

  //googlepay api caller
  static async getGooglePayDataForSpecificSessionId(sessionId, ctx = {}) {
    try {
      const res = await axios.get(
        `${process.env.MICROSERVICES_URL}api/googlepay/v1/donationDetail?id=${sessionId}`,
        {
          headers: {
            authorization: `Bearer ${process.env.GOOGLE_PAY_AUTHENTICATION_TOKEN}`,
            "content-type": 'application/json',
          }
        });
      if (res.status !== 200) {
        return null;
      }
      const data = res.data;
      return data;
    } catch (e) {
      captureException(e, {});
      return null;
    }
  }

  static async getHomepage(variant, ctx = {}) {
    // isomorphic-fetch runs in browsers and server-side
    let data = "";
    try {
      const res = await awesomeFetch(
        `api/v1.1/homepage?variant=${variant}`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getMissions(slug, ctx = {}) {
    // isomorphic-fetch runs in browsers and server-side
    let data = "";

    try {
      const res = await awesomeFetch(
        `api/v1.1/campaigns_programs/${slug}`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getDonation(id, ctx = {}) {
    let data = "";
    try {
      const res = await awesomeFetch(`api/v1.1/donation/${id}`, ctx);
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getNonprofitPrograms(ngoId, ctx = {}) {
    let data = "";
    try {
      const res = await awesomeFetch(
        `api/v1.1/nonprofit/nonprofitPrograms/${ngoId}`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getProgram(slug, userId, utm_source, cookies, ctx = {}) {
    // isomorphic-fetch runs in browsers and server-side
    let data = "";
    try {
      const res = await awesomeFetch(
        `api/v1.1/program/${slug}?userId=${userId}&utmSource=${utm_source}&userCookies=${cookies}`,
        ctx
      );
      data = await res.json();
    } catch (e) {

      captureException(e, {});
    }

    return data;
  }

  static async getActiveDonors(id, ctx = {}) {
    let data = "";
    try {
      const res = await awesomeFetch(
        `api/v1.1/program/${id}/activeSubscriptions`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }

    return data;
  }

  static async getActiveBeneficiariesSupported(id, ctx = {}) {
    let data = "";
    try {
      const res = await awesomeFetch(
        `api/v1.1/program/${id}/activeQuantity`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }

    return data;
  }

  static async getInitSearch(ctx = {}) {
    let data = "";
    try {
      const res = await awesomeFetch(
        `api/v1.1/search/program?tag=&count=0&searchType=relevance`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async checkIfPANRequired(currentDonationAmount, ctx = {}) {
    // isomorphic-fetch runs in browsers and server-side
    try {
      const res = await awesomeFetch(
        `api/v1.1/checkout/panrequirement?donationAmount=${currentDonationAmount}`,
        ctx
      );
      const data = res.json();
      return data;
    } catch (e) {
      captureException(e, {});
      return null;
    }
  }

  static async getSearchTagResults(jsonParams, ctx = {}) {
    try {
      const res = await awesomeFetch(
        `/api/v1.1/search/program?${createQueryFromJson(jsonParams)}`,
        ctx
      );
      const data = res.json();
      return data;
    } catch (e) {
      captureException(e, {});
    }
  }

  static async getRecommendedPrograms(tag, ctx = {}) {
    try {
      const res = await awesomeFetch(
        `/api/v1.1/search/recommendedPrograms?tag=${encodeURIComponent(tag)}`,
        ctx
      );
      return await res.json();
    } catch (e) {
      captureException(e, ctx);
      return null;
    }
  }

  static async getProgramsByName(names, ctx = {}) {
    try {
      const nameString = encodeURIComponent(names.map(name => name).join("|"));
      const res = await awesomeFetch(
        `/api/v1.1/search/program/byname?names=${nameString}`,
        ctx
      );
      const data = res.json();
      return data;
    } catch (e) {
      captureException(e, {});
    }
  }

  static async getCategoryTags(categories, ctx = {}) {
    let data = "";
    try {
      categories = categories.replace(" ", "_");
      const res = await awesomeFetch(
        `/api/v1.1/search/categorytags?category=${encodeURIComponent(
          categories
        )}`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getUserDashboard(ctx = {}) {
    const data = {};
    try {
      const res = await awesomeFetch(`api/v1.1/userdashboard/user`, ctx);
      data.profile = await res.json();
      const res1 = await awesomeFetch(`api/v1.1/userdashboard/donation`, ctx);
      const donations = await res1.json();
      data.donations = donations && donations.length > 0 ? donations : null;
      const res2 = await awesomeFetch(
        `api/v1.1/userdashboard/userbeneficiaries`,
        ctx
      );
      data.beneficiaries = await res2.json();
      const res3 = await awesomeFetch(
        `api/v1.1/userdashboard/getCertificates80GData`,
        ctx
      );
      data.certificates = await res3.json();
      const res4 = await awesomeFetch("/api/v1.1/feedbackReport", ctx);
      data.reports = await res4.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getConsolidatedReceipt(startDate, endDate, ctx = {}) {
    let data = {};
    try {
      const res = await awesomeFetch(
        `api/v1.1/userdashboard/getCertificates80GData/consolidated?startDate=${startDate}&endDate=${endDate}`,
        ctx
      );
      data = await res.json();
      data.statusCode = res.status;
    } catch (e) {
      console.log('getConsolidatedReceipt', e);
      captureException(e, {});
    }
    return data;
  }

  static async getAllSearchTags(ctx = {}) {
    let data = {};
    try {
      const res = await awesomeFetch(`api/v1.1/search/searchtags/`, ctx);
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getCampaign(slug, ctx = {}) {
    let data = "";
    try {
      const res = await awesomeFetch(`api/v1.1/campaign/${slug}`, ctx);
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getUserData(ctx = {}) {
    const data = {};
    try {
      const res = await awesomeFetch(`api/v1.1/userdashboard/user`, ctx);
      data.userInfo = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getUserDataByUserId(id, ctx = {}) {
    const data = {};
    try {
      const res = await awesomeFetch(`api/v1.1/userdashboard/user/getUserByUserId?id=${id}`, ctx);
      data.userInfo = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getNonprofit(ngoSlug, fromProgramPage, ngoJson, ctx = {}) {
    let data = {};
    try {
      const res = await awesomeFetch(
        `api/v1.1/nonprofit/${encodeURIComponent(ngoSlug)}`,
        ctx
      );
      if (res.status !== 200) {
        return null;
      }
      data = await res.json();
      if (fromProgramPage) {
        data = await {
          ...data,
          fromProgramPage: true,
          prevPage: { ...ngoJson }
        };
      } else {
        data = await { ...data, fromProgramPage: false };
      }
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getUserWishList(userId, programId, ctx = {}) {
    let data = {};
    try {
      const res = await awesomeFetch(
        `api/v1.1/wishlist/users/${userId}/programs/${programId}`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getUpdateCardData(subscriptionId, ctx = {}) {
    let data = {};
    try {
      const res = await awesomeFetch(
        `/api/v1.1/razorpayCardUpdate/${subscriptionId}`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getFaqList(ctx = {}) {
    let data = {};
    try {
      const res = await awesomeFetch(`api/v1.1/faqs/reterieve`, ctx);
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async getWishListItemCount(programId, ctx = {}) {
    let data = { count: 0 };
    try {
      const res = await awesomeFetch(
        `api/v1.1/wishlist/programs/${programId}`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data.count;
  }

  static async fetchBeneficiaryInfo(beneficiaryHashedId) {
    try {
      const res = await axios({
        method: "get",
        url: `/api/v1.1/landingPage/social-share/beneficiaryInfo?id=${beneficiaryHashedId}`,
        responseType: "json"
      });
      return res.data.data;
    } catch (e) {
      console.log("ERROR while fetching beneficiary Info", e);
      captureException(e, {});
    }
  }

  static async fetchBeneficiaryUpdate(beneficiaryHashedId) {
    try {
      const res = await axios({
        method: "get",
        url: `/api/v1.1/landingPage/social-share/beneficiaryUpdate?id=${beneficiaryHashedId}`,
        responseType: "json"
      });
      return res.data.data;
    } catch (e) {
      console.log("ERROR while fetching beneficiary Info", e);
      captureException(e, {});
    }
  }
  static async getListofCertifiedNGOs(ctx) {
    let data = {};
    try {
      const res = await awesomeFetch(`api/v1/ngolist/nonprofit`, ctx);
      data = await res.json();
    } catch (e) {
      captureException(e, {});
    }
    return data;
  }

  static async checkForgotPasswordCode(code, ctx) {
    let data = {};
    try {
      const res = await awesomeFetch(
        `/api/v1.1/forgotPassword/code?code=${code}`,
        ctx
      );
      data = await res.json();
    } catch (e) {
      captureException(e, {});
      return { error: "Something went wrong in verifying code" };
    }
    return data;
  }

  static async fetchFUCData(key, ctx = {}) {
    const res = await awesomeFetch(
      `/api/v1.1/fuc?secretKey=${encodeURIComponent(key)}`,
      ctx
    );
    return res.json();
  }

  static async relatedProgram(programId, cause, ctx = {}) {
    let data = {};
    try {
      const res = await awesomeFetch(
        `/api/v1.1/relatedProgram/find?programId=${programId}&cause=${cause}`,
        ctx
      );
      const resJson = await res.json();
      data = resJson.data;
    } catch (e) {
      captureException(e, {});
      return { error: "Somethings went wrong in Related Programs " };
    }
    return data;
  }

  static async getWGDashboard(selectedMonth, selectedYear, ctx = {}) {
    let data = {},
      month,
      year;
    const today = new Date();
    if (!selectedMonth || !selectedYear) {
      month = today.getMonth() + 1;
      year = today.getFullYear();
    } else {
      month = selectedMonth;
      year = selectedYear;
    }

    try {
      const res = await awesomeFetch(
        `/api/v1.1/wg/prdsDashboard?month=${month}&&year=${year}`,
        ctx
      );
      const resJson = await res.json();
      data = resJson.data;
    } catch (e) {
      captureException(e, {});
      return { error: "Somethings went wrong ", e };
    }
    return data;
  }

  // Fetch utr's
  static async fetchUTRForMonth(selectedMonth, selectedYear, ctx = {}) {
    let data = [];
    if (!selectedMonth || !selectedYear) {
      const today = new Date();
      selectedMonth = today.getMonth() + 1;
      selectedYear = today.getFullYear();
    }

    try {
      const res = await awesomeFetch(
        `/api/v1.1/wg/prdsDashboard/utr?month=${selectedMonth}&&year=${selectedYear}`,
        ctx
      );
      const resJson = await res.json();
      if (resJson.success) {
        data = resJson.data;
      }
    } catch (e) {
      captureException(e, {});
      return { error: "Somethings went wrong ", e };
    }
    return data;
  }

  static async ngoListByLocation(stateSlug, districtSlug, ctx = {}) {
    if (!stateSlug) {
      return [];
    }

    let url = `/api/v1.1/ngoDirectory/ngosByLocation?stateSlug=${encodeURIComponent(
      stateSlug
    )}`;

    if (districtSlug) {
      url = url + `&districtSlug=${encodeURIComponent(districtSlug)}`;
    }

    try {
      const res = await awesomeFetch(url, ctx);
      return await res.json();
    } catch (e) {
      captureException(e, {});
      return [];
    }
  }

  static async stateWiseSummary(ctx = {}) {
    let url = `/api/v1.1/ngoDirectory/stateWiseSummary`;
    try {
      const res = await awesomeFetch(url, ctx);
      return await res.json();
    } catch (e) {
      captureException(e, {});
      return [];
    }
  }

  static async ngoDirectoryRecommendedPrograms(stateSlug, ctx = {}) {
    let url = `/api/v1.1/ngoDirectory/recommendedProgram`;
    if (stateSlug) {
      url = url + `?stateSlug=${encodeURIComponent(stateSlug)}`;
    }

    try {
      const res = await awesomeFetch(url, ctx);
      return await res.json();
    } catch (e) {
      captureException(e, {});
      return [];
    }
  }

  static async getPartnerDetails(slug, ctx = {}) {
    let url = `/api/v1.1/partners`;
    if (slug) {
      url = url + `?slug=${encodeURIComponent(slug)}`;
    }

    try {
      const res = await awesomeFetch(url, ctx);
      return await res.json();
    } catch (e) {
      captureException(e, {});
      return [];
    }
  }
	static createFullUrl(url, ctx) { return createFullURL(url, ctx); }

	static async getRecommendedProgramForForm(ctx = {}) {
		// isomorphic-fetch runs in browsers and server-side
		try {
			const res = await awesomeFetch(`api/v1.1/recommended/program`, ctx);
			const data = res.json();
			return data;
		} catch (e) {
			captureException(e, {});
			return null;
		}
	}

	// Added for the mission.js page
	static async getMission(slug, ctx = {}) {
		// isomorphic-fetch runs in browsers and server-side
		try {
			const res = await awesomeFetch(`api/v1.1/mission/${slug}`, ctx);
			const data = res.json();
			return data;
		} catch (e) {
			captureException(e, {});
			return null;
		}
	}

	//your cause api caller
	static async getYourCauseDataForSpecificSessionId(sessionId, ctx = {}) {
		try {
			const res = await awesomeFetch(`api/yc/fetchData/${sessionId}`, ctx);
			const data = res.json();
			return data;
		} catch (e) {
			captureException(e, {});
			return null;
		}
	}

	static async getHomepage(variant, ctx = {}) {
		// isomorphic-fetch runs in browsers and server-side
		let data = '';
		try {
			const res = await awesomeFetch(`api/v1.1/homepage?variant=${variant}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getDonation(id, ctx = {}) {
		let data = '';
		try {
			const res = await awesomeFetch(`api/v1.1/donation/${id}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getNonprofitPrograms(ngoId, ctx = {}) {
		let data = '';
		try {
			const res = await awesomeFetch(`api/v1.1/nonprofit/nonprofitPrograms/${ngoId}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getProgram(slug, userId, utm_source, cookies, ctx = {}) {
		// isomorphic-fetch runs in browsers and server-side
		let data = '';
		try {
			const res = await awesomeFetch(`api/v1.1/program/${slug}?userId=${userId}&utmSource=${utm_source}&userCookies=${cookies}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}

		return data;
	}

	static async getActiveDonors(id, ctx={}) {
		let data = '';
		try {
			const res = await awesomeFetch(`api/v1.1/program/${id}/activeSubscriptions`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}

		return data;
	}

	static async getActiveBeneficiariesSupported(id, ctx={}) {
		let data = '';
		try {
			const res = await awesomeFetch(`api/v1.1/program/${id}/activeQuantity`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}

		return data;
	}

	static async getInitSearch(ctx = {}) {
		let data = '';
		try {
			const res = await awesomeFetch(`api/v1.1/search/program?tag=&count=0&searchType=relevance`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async checkIfPANRequired(currentDonationAmount, ctx = {}) {
		// isomorphic-fetch runs in browsers and server-side
		try {
			const res = await awesomeFetch(`api/v1.1/checkout/panrequirement?donationAmount=${currentDonationAmount}`, ctx);
			const data = res.json();
			return data;
		} catch (e) {
			captureException(e, {});
			return null;
		}
	}

	static async getSearchTagResults(jsonParams, ctx = {}) {
		try {
			const res = await awesomeFetch(`/api/v1.1/search/program?${createQueryFromJson(jsonParams)}`, ctx);
			const data = res.json();
			return data;
		} catch (e) {
			captureException(e, {});
		}
	}

	static async getRecommendedPrograms(tag, ctx = {}) {
		try {
			const res = await awesomeFetch(`/api/v1.1/search/recommendedPrograms?tag=${encodeURIComponent(tag)}`, ctx);
			return await res.json();
		} catch (e) {
			captureException(e, ctx);
			return null;
		}
	}

	static async getProgramsByName(names, ctx = {}) {
		try {
			const nameString = encodeURIComponent(names.map(name => name).join("|"));
			const res = await awesomeFetch(`/api/v1.1/search/program/byname?names=${nameString}`, ctx);
			const data = res.json();
			return data;
		} catch (e) {
			captureException(e, {});
		}
	}

	static async getCategoryTags(categories, ctx = {}) {
		let data = '';
		try {
			categories = categories.replace(' ', '_');
			const res = await awesomeFetch(`/api/v1.1/search/categorytags?category=${encodeURIComponent(categories)}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getUserDashboard(ctx = {}) {
		const data = {};
		try {
			const res = await awesomeFetch(`api/v1.1/userdashboard/user`, ctx);
			data.profile = await res.json();
			const res1 = await awesomeFetch(`api/v1.1/userdashboard/donation`, ctx);
			const donations = await res1.json();
			data.donations = (donations && donations.length > 0) ? donations : null;
			const res2 = await awesomeFetch(`api/v1.1/userdashboard/userbeneficiaries`, ctx);
			data.beneficiaries = await res2.json();
			const res3 = await awesomeFetch(`api/v1.1/userdashboard/getCertificates80GData`, ctx);
			data.certificates = await res3.json();
			const res4 = await awesomeFetch('/api/v1.1/feedbackReport', ctx);
			data.reports = await res4.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getConsolidatedReceipt(startDate, endDate, ctx = {}) {
		// const data = {};
		// try {
		// 	const res = await awesomeFetch(`api/v1.1/userdashboard/getCertificates80GData/consolidated?startDate=${startDate}&endDate=${endDate}`, ctx);
		// 	data.consolidatedCertficate = await res.json();
		// } catch (e) {
		// 	captureException(e, {});
		// }
    // return data;    
    let data = {};
    try {
      const res = await awesomeFetch(
        `api/v1.1/userdashboard/getCertificates80GData/consolidated?startDate=${startDate}&endDate=${endDate}`,
        ctx
      );
      data = await res.json();
      data.statusCode = res.status;
    } catch (e) {
      console.log('getConsolidatedReceipt', e);
      captureException(e, {});
    }
    return data;
	}

	// static async getAllSearchTags(ctx = {}) {
	// 	let data = {};
	// 	try {
	// 		const res = await awesomeFetch(`api/v1.1/search/searchtags`, ctx);
	// 		data = await res.json();
	// 	} catch (e) {
	// 		captureException(e, {});
	// 	}
	// 	return data;
	// }

	static async getCampaign(slug, ctx = {}) {
		let data = '';
		try {
			const res = await awesomeFetch(`api/v1.1/campaign/${slug}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getUserData(ctx = {}) {
		const data = {};
		try {
			const res = await awesomeFetch(`api/v1.1/userdashboard/user`, ctx);
			data.userInfo = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getNonprofit(ngoSlug, fromProgramPage, ngoJson, ctx = {}) {
		let data = {};
		try {
			const res = await awesomeFetch(`api/v1.1/nonprofit/${encodeURIComponent(ngoSlug)}`, ctx);
			if (res.status !== 200) {
				return null;
			}
			data = await res.json();
			if (fromProgramPage) {
				data = await { ...data, fromProgramPage: true, prevPage: { ...ngoJson } };
			} else {
				data = await { ...data, fromProgramPage: false };
			}
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getUserWishList(userId, programId, ctx = {}) {
		let data = {};
		try {
			const res = await awesomeFetch(`api/v1.1/wishlist/users/${userId}/programs/${programId}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getUpdateCardData(subscriptionId, ctx = {}) {
		let data = {};
		try {
			const res = await awesomeFetch(`/api/v1.1/razorpayCardUpdate/${subscriptionId}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getFaqList(ctx = {}) {
		let data = {};
		try {
			const res = await awesomeFetch(`api/v1.1/faqs/reterieve`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async getWishListItemCount(programId, ctx = {}) {
		let data = { count: 0 };
		try {
			const res = await awesomeFetch(`api/v1.1/wishlist/programs/${programId}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data.count;
	}

	static async fetchBeneficiaryInfo(beneficiaryHashedId) {
		try {
			const res = await axios({
				method: 'get',
				url: `/api/v1.1/landingPage/social-share/beneficiaryInfo?id=${beneficiaryHashedId}`,
				responseType: 'json',
			});
			return res.data.data;
		} catch (e) {
			console.log('ERROR while fetching beneficiary Info', e);
			captureException(e, {})
		}
	}

	static async fetchBeneficiaryUpdate(beneficiaryHashedId) {
		try {
			const res = await axios({
				method: 'get',
				url: `/api/v1.1/landingPage/social-share/beneficiaryUpdate?id=${beneficiaryHashedId}`,
				responseType: 'json',
			});
			return res.data.data;
		} catch (e) {
			console.log('ERROR while fetching beneficiary Info', e);
			captureException(e, {})
		}
	}
	static async getListofCertifiedNGOs(ctx) {
		let data = {};
		try {
			const res = await awesomeFetch(`api/v1/ngolist/nonprofit`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
		}
		return data;
	}

	static async checkForgotPasswordCode(code, ctx) {
		let data = {};
		try {
			const res = await awesomeFetch(`/api/v1.1/forgotPassword/code?code=${code}`, ctx);
			data = await res.json();
		} catch (e) {
			captureException(e, {});
			return { error: 'Something went wrong in verifying code' };
		}
		return data;

	}

	static async fetchFUCData(key, ctx = {}) {
		const res = await awesomeFetch(`/api/v1.1/fuc?secretKey=${encodeURIComponent(key)}`, ctx);
		return res.json();
	}

	static async relatedProgram(programId, cause, ctx = {}) {
		let data = {};
		try {
			const res = await awesomeFetch(`/api/v1.1/relatedProgram/find?programId=${programId}&cause=${cause}`, ctx);
			const resJson = await res.json();
			data = resJson.data;
		}
		catch (e) {
			captureException(e, {});
			return { error: 'Somethings went wrong in Related Programs ' }
		}
		return data;
	}

	static async getWGDashboard(selectedMonth, selectedYear, ctx = {}) {
		let data = {}, month, year;
		const today = new Date();
		if(!selectedMonth || !selectedYear) {
			month = today.getMonth() + 1;
			year = today.getFullYear();
		} else {
			month = selectedMonth;
			year = selectedYear;
		}

		try {
			const res = await awesomeFetch(`/api/v1.1/wg/prdsDashboard?month=${month}&&year=${year}`, ctx);
			const resJson = await res.json();
			data = resJson.data;
		}
		catch (e) {
			captureException(e, {});
			return { error: 'Somethings went wrong ', e }
		}
		return data;
	}

	static async getWGDashboardSummary(ctx = {}) {
		let data={};
		try {
			const res = await awesomeFetch(`/api/v1.1/wg/prdsDashboard/summary`, ctx);
			data = await res.json();
		}
		catch (e) {
			captureException(e, {});
			return { error: 'Somethings went wrong ', e }
		}
		return data;
	}

	// Fetch utr's
	static async fetchUTRForMonth(selectedMonth, selectedYear, ctx={}) {
		let data = [];
		if (!selectedMonth || !selectedYear) {
			const today = new Date();
			selectedMonth = today.getMonth() + 1;
			selectedYear = today.getFullYear();
		}

		try {
			const res = await awesomeFetch(`/api/v1.1/wg/prdsDashboard/utr?month=${selectedMonth}&&year=${selectedYear}`, ctx);
			const resJson = await res.json();
			if (resJson.success) {
				data = resJson.data;
			}
		} catch (e) {
			captureException(e, {});
			return { error: 'Somethings went wrong ', e }
		}
		return data;
	}

	static async ngoListByLocation(stateSlug, districtSlug, ctx = {}) {

		if (!stateSlug) {
			return [];
		}

		let url = `/api/v1.1/ngoDirectory/ngosByLocation?stateSlug=${encodeURIComponent(stateSlug)}`;

		if (districtSlug) {
			url = url + `&districtSlug=${encodeURIComponent(districtSlug)}`;
		}

		try {
			const res = await awesomeFetch(url, ctx);
			return await res.json();
		}
		catch (e) {
			captureException(e, {});
			return [];
		}
	}

	static async stateWiseSummary(ctx = {})  {
		let url = `/api/v1.1/ngoDirectory/stateWiseSummary`;
		try {
			const res = await awesomeFetch(url, ctx);
			return await res.json();
		} catch (e) {
			captureException(e, {});
			return [];
		}
	}

	static async ngoDirectoryRecommendedPrograms(stateSlug, ctx = {}) {
		let url = `/api/v1.1/ngoDirectory/recommendedProgram`;
		if (stateSlug) {
			url = url + `?stateSlug=${encodeURIComponent(stateSlug)}`;
		}

		try {
			const res = await awesomeFetch(url, ctx);
			return await res.json();
		}
		catch (e) {
			captureException(e, {});
			return [];
		}
	}

	static async getPartnerDetails(slug, ctx = {}) {
		let url = `/api/v1.1/partners`;
		if (slug) {
			url = url + `?slug=${encodeURIComponent(slug)}`;
		}

		try {
			const res = await awesomeFetch(url, ctx);
			return await res.json();
		}
		catch (e) {
			captureException(e, {});
			return [];
		}
	}

  static async getWgDonations(ctx = {}) {
    let url = `/api/v1.1/wg/signup/programDetail`;
    try {
      const res = await awesomeFetch(url, ctx);
      return await res.json();
    } catch (e) {
      captureException(e, {});
      return [];
    }
  }

  static async checkCardUpdateCode(code, ctx) {
    let data = {};
    try {
      const res = await awesomeFetch(
        `/api/v1.1/card/stripe/check-card-update-code/?code=${code}`,
        ctx
    )
      data = await res.json();

      if(res.status !== 200) {
        throw new Error(data.message);
      }
    } catch (e) {
      captureException(e, {});     
      return { error: `Something went wrong: ${e.message}` };
    }
    return data;
  }
}
